import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import Tooltip from "./Tooltip.js";
import * as types from "../../constants/ComponentConstants.js";
import ValidationMessage from "./ValidationMessage.js";
import Row from "./Row.js";
import Loader from "./Loader.js";
import documentApi from "../../api/shared/documentApi";

const ReadonlyFiles = (props) => {
  const { componentName, ownerId, size, center } = props;
  const [files, setFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fileSessionId, setFileSessionId] = useState(null);

  useEffect(() => {
    beginSession(ownerId).then((sessionId) => {
      setFileSessionId(sessionId);
      fetchFiles(sessionId, componentName);
    });
  }, [ownerId, componentName]);

  const beginSession = async (ownerId) => {
    try {
      const response = await documentApi.beginSession(ownerId, "");
      return response.data;
    } catch (error) {
      return null;
    }
  };

  const fetchFiles = async (sessionId, componentName) => {
    setIsLoading(true);
    try {
      const response = await documentApi.listContentWithoutCache(ownerId, sessionId, componentName);
      const componentFolder = response.data.find((d) => d.name === componentName);

      if (componentFolder) {
        const fetchedFiles = componentFolder.files.map((file) => file.name);

        setFiles(fetchedFiles);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getFileDownloadUrl = () => {
    return documentApi.getFileDownloadUrl(fileSessionId);
  };

  const hasFiles = files.length > 0;
  const tooltipType = props.tooltipType || types.TOOLTIP_INFO;
  const showSpinner = isLoading;

  let info = props.tooltip ? (
    <Tooltip
      text={props.tooltip}
      tooltipType={tooltipType}
      iconColor={props.tooltipIconColor}
      windowWidth={props.tooltipWindowWidth}
    />
  ) : null;

  const centerDiv = <div className="o-col-1">&nbsp;</div>;
  const sizeClassName = center ? "o-col-10" : size ? `o-col-${size}` : "o-col-12";

  let body;
  if (showSpinner) {
    body = <Loader />;
  } else {
    body = !hasFiles ? "Nog geen documenten beschikbaar" : null;
  }

  const fileList = hasFiles ? (
    <ul className="filelist">
      {files.map((file, i) => {
        const path = `${getFileDownloadUrl()}/${componentName.replace("/", "$")}/${file}`;
        return (
          <li key={file}>
            <a href={path}>{file}</a>
          </li>
        );
      })}
    </ul>
  ) : null;

  let cFicheClassNames = {
    "c-fiche": true,
    [props.color]: true,
    "c-fiche--downloaduploadfiles": true,
    disabled: true,
    error: !!props.validationMessage,
    warning:
      !!props.validationMessage &&
      props.validationMessageType === types.ValidationMessageTypes.WARNING,
  };

  let divClassname = "";
  if (!showSpinner) divClassname = "c-fiche__body";

  const component = (
    <div className={classnames(sizeClassName)}>
      <div className={classnames("c-fiche-wrap", "u-clear")}>
        <div className={classnames(cFicheClassNames)}>
          <header className="c-fiche__header">
            <h1 className="title">
              <span
                dangerouslySetInnerHTML={{
                  __html: props.title + (props.optional ? " (Optioneel)" : ""),
                }}
              ></span>
              {info}
            </h1>
          </header>
          <div className={divClassname}>
            {body}
            {fileList}
          </div>
        </div>
      </div>
      {props.validationMessage && (
        <ValidationMessage
          message={props.validationMessage}
          type={props.validationMessageType}
        />
      )}
    </div>
  );

  return center ? <Row>{centerDiv}{component}</Row> : component;
};

ReadonlyFiles.propTypes = {
  title: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
  componentName: PropTypes.string.isRequired,
  ownerId: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  size: PropTypes.number,
  center: PropTypes.bool,
  optional: PropTypes.bool,
  tooltip: PropTypes.string,
  tooltipType: PropTypes.string,
  tooltipIconColor: PropTypes.string,
  tooltipWindowWidth: PropTypes.number,
  validationMessage: PropTypes.string,
  validationMessageType: PropTypes.string,
};

export default ReadonlyFiles;
